import { css } from '@emotion/css';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigator } from '../../routing/navigator';
import { CheckCircleSolid } from '../../shared/assets/icons';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../shared/components/Button';
import { Layout } from '../../shared/components/layout/Layout';
import { Colors } from '../../shared/styles/colors';
import { HEADER_HEIGHT } from '../../shared/styles/css-values';
import { doctrineStencilStyle, p2Style } from '../../shared/styles/text';

const SuccessPaymentSection: React.FC<{ balance: string }> = ({ balance }) => {
  return (
    <section className={successfulSectionStyle}>
      You successfully added
      <span className={currencyAmountStyle}>{` ${balance} `}</span>
      to your account.
    </section>
  );
};

const SuccessReservationSection: React.FC<{ amount: string }> = ({
  amount,
}) => {
  return (
    <section className={successfulSectionStyle}>
      You successfully reserved
      <span className={currencyAmountStyle}>{` ${amount} `}</span>
      on your card.
      <p className={reservationExplanationStyle}>
        Nothing has been charged yet. Use the pedestals as usual — your sessions
        are covered by this reservation, and when they end you are charged only
        for what you actually used. The rest of the reserved amount is released
        back to your card.
      </p>
    </section>
  );
};

const SuccessSignUpSection: React.FC = () => {
  return (
    <section className={successfulSectionStyle}>
      You successfully created your account.
    </section>
  );
};

export const SuccessPage = (): JSX.Element => {
  const navigator = useNavigator();
  const [urlParams] = useSearchParams();

  const balance = urlParams.get('Amount');
  // A card reservation is not a top-up: nothing has been charged, so the page
  // has to explain how the money is going to be spent instead.
  const isReservation = urlParams.get('TransactionType') === 'PREAUTHORIZATION';

  return (
    <Layout.Content className={pageStyle}>
      <div className={innerContainerStyle}>
        <div className={iconWrapStyle}>
          <CheckCircleSolid className={checkCircleIconStyle} />
        </div>
        {balance ? (
          isReservation ? (
            <SuccessReservationSection amount={`${balance} EUR`} />
          ) : (
            <SuccessPaymentSection balance={`${balance} EUR`} />
          )
        ) : (
          <SuccessSignUpSection />
        )}
      </div>
      <Button
        text={"let's go"}
        size={ButtonSizeEnum.LARGE}
        variant={ButtonVariantEnum.FILLED}
        onClick={navigator.toLanding}
        fullWidth
      />
    </Layout.Content>
  );
};

const pageStyle = css`
  min-height: calc(100vh - ${HEADER_HEIGHT});
  display: flex;
  flex-direction: column;
  padding-bottom: calc(2rem + env(safe-area-inset-bottom, 0px));
`;

const innerContainerStyle = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  gap: 2rem;
  padding-bottom: 2rem;
`;

const successfulSectionStyle = css`
  text-align: center;
  color: ${Colors.CAPRI_BLUE};
  font-weight: bold;
  line-height: 130%;
  text-wrap: balance;
`;

const reservationExplanationStyle = css`
  ${p2Style}
  margin-top: 1rem;
  font-weight: normal;
  color: ${Colors.TEXT_SECONDARY};
  text-wrap: pretty;
`;

const iconWrapStyle = css`
  width: 9rem;
  height: 9rem;
  border-radius: 50%;
  background: ${Colors.SUCCESS_TINT};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const checkCircleIconStyle = css`
  width: 5.5rem;
  height: 5.5rem;
`;

const currencyAmountStyle = css`
  ${doctrineStencilStyle}
  font-size: 1.5rem;
`;
